import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchHelpArticle } from "../services/helpService";
import type { HelpArticleDetail } from "../types/help";
import { hideFailedHelpImages, sanitizeHelpHtml } from "../utils/helpHtml";
import "../styles/getting-started.css";

const LOAD_ERROR_MESSAGE =
    "Unable to load getting started articles. Please try again shortly.";
const NOT_FOUND_MESSAGE = "We couldn’t find that article.";

export default function GettingStartedArticlePage() {
    const { slug } = useParams<{ slug: string }>();
    const [article, setArticle] = useState<HelpArticleDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState("");

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            if (!slug) {
                setNotFound(true);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setNotFound(false);
            setLoadError(false);
            setArticle(null);
            setActiveSectionId("");

            try {
                const result = await fetchHelpArticle(slug);
                if (!isMounted) {
                    return;
                }
                if (result === null) {
                    setNotFound(true);
                } else {
                    setArticle(result);
                    setActiveSectionId(result.sections[0]?.id ?? "");
                }
            } catch {
                if (isMounted) {
                    setLoadError(true);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        void load();
        return () => {
            isMounted = false;
        };
    }, [slug]);

    const sanitizedHtml = useMemo(
        () => (article?.contentHtml ? sanitizeHelpHtml(article.contentHtml) : ""),
        [article?.contentHtml],
    );

    useEffect(() => {
        if (!article?.sections.length) {
            return undefined;
        }
        if (typeof IntersectionObserver === "undefined") {
            return undefined;
        }

        const headings = article.sections
            .map((section) => document.getElementById(section.id))
            .filter((element): element is HTMLElement => element != null);
        if (headings.length === 0) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top);
                const nextId = visible[0]?.target.id;
                if (nextId) {
                    setActiveSectionId(nextId);
                }
            },
            { rootMargin: "-20% 0px -65% 0px", threshold: [0, 1] },
        );

        headings.forEach((heading) => observer.observe(heading));
        return () => observer.disconnect();
    }, [article, sanitizedHtml]);

    const handleTocClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        const href = event.currentTarget.getAttribute("href");
        const nextId = href?.startsWith("#") ? href.slice(1) : "";
        if (nextId) {
            setActiveSectionId(nextId);
        }
    }, []);

    const articleBodyRef = useCallback((node: HTMLDivElement | null) => {
        if (node) {
            hideFailedHelpImages(node);
        }
    }, [sanitizedHtml]);

    return (
        <main className="getting-started-page getting-started-article-page" data-testid="getting-started-article-page">
            <Link
                to="/getting-started"
                className="getting-started-article-back"
                data-testid="getting-started-article-back"
                aria-label="Back to Getting started"
            >
                <svg
                    className="getting-started-article-back-icon"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                >
                    <path
                        d="M10.25 3.25 5.75 8l4.5 4.75"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                Back
            </Link>

            <nav className="getting-started-breadcrumb" aria-label="Breadcrumb" data-testid="getting-started-breadcrumb">
                <Link to="/getting-started" className="getting-started-breadcrumb-link">
                    Getting started
                </Link>
                {article && (
                    <>
                        <span className="getting-started-breadcrumb-separator" aria-hidden="true">
                            /
                        </span>
                        <span className="getting-started-breadcrumb-current">{article.title}</span>
                    </>
                )}
            </nav>

            {isLoading && (
                <p className="getting-started-status" data-testid="getting-started-loading">
                    Loading article…
                </p>
            )}

            {!isLoading && loadError && (
                <p className="getting-started-status getting-started-status--error" data-testid="getting-started-error">
                    {LOAD_ERROR_MESSAGE}
                </p>
            )}

            {!isLoading && notFound && (
                <div className="getting-started-status" data-testid="getting-started-not-found">
                    <p>{NOT_FOUND_MESSAGE}</p>
                </div>
            )}

            {!isLoading && article && (
                <div className="getting-started-article-layout">
                    <article className="getting-started-article">
                        <h1 className="getting-started-article-title">{article.title}</h1>
                        {article.summary && (
                            <p className="getting-started-article-summary">{article.summary}</p>
                        )}
                        <div
                            ref={articleBodyRef}
                            className="getting-started-article-body"
                            data-testid="getting-started-article-body"
                            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                        />
                    </article>
                    {article.sections.length > 0 && (
                        <aside className="getting-started-toc" data-testid="getting-started-toc" aria-label="On this page">
                            <ul>
                                {article.sections.map((section) => (
                                    <li
                                        key={section.id}
                                        className={
                                            section.id === activeSectionId
                                                ? "getting-started-toc-item is-active"
                                                : "getting-started-toc-item"
                                        }
                                    >
                                        <a href={`#${section.id}`} onClick={handleTocClick}>
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </aside>
                    )}
                </div>
            )}
        </main>
    );
}
